import {
  Box,
  Text,
  Link as UiLink,
  Heading,
  Spinner,
  Flex,
  VisuallyHidden,
} from '@everlywell/ui-kit';
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import usePdfViewer from '../../hooks/usePdfViewer';
import useWindowSize from '../../hooks/useWindowSize';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.mjs`;

interface PDFViewerProps {
  url: string;
  title?: string;
  initialPage?: number;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  url,
  title,
  initialPage = 1,
}) => {
  const { isLoading, isError, errorType, handleError, retryLoading } =
    usePdfViewer({ url });
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(initialPage);
  const [useIframeFallback, setUseIframeFallback] = useState<boolean>(false);
  const { width, height } = useWindowSize();
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    setUseIframeFallback(false);
    setPageNumber(initialPage);
  }, [url, initialPage]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!containerRef.current?.contains(document.activeElement)) return;

      if (e.key === 'ArrowLeft' && pageNumber > 1) {
        e.preventDefault();
        setPageNumber((prev) => prev - 1);
      } else if (e.key === 'ArrowRight' && numPages && pageNumber < numPages) {
        e.preventDefault();
        setPageNumber((prev) => prev + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pageNumber, numPages]);

  const documentOptions = useMemo(
    () => ({
      cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
      cMapPacked: true,
    }),
    [],
  );

  const onDocumentLoadSuccess = useCallback(
    (pdf: any): void => {
      setNumPages(pdf.numPages);

      if (pageNumber > pdf.numPages) {
        setPageNumber(initialPage);
      }

      try {
        pdf
          .getPage(1)
          .then((page: any) => {
            const viewport = page.getViewport({ scale: 1.0 });
            setPdfDimensions({
              width: viewport.width,
              height: viewport.height,
            });
          })
          .catch((error: Error) => {
            // Error getting page dimensions
            setPdfDimensions({ width: 595, height: 842 });
          });
      } catch (error) {
        // Error accessing PDF dimensions
        setPdfDimensions({ width: 595, height: 842 });
      }
    },
    [pageNumber, initialPage],
  );

  const onDocumentLoadError = useCallback((error: Error): void => {
    setUseIframeFallback(true);
  }, []);

  const changePage = useCallback((offset: number): void => {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  }, []);

  const previousPage = useCallback((): void => {
    changePage(-1);
  }, [changePage]);

  const nextPage = useCallback((): void => {
    changePage(1);
  }, [changePage]);

  const renderIframeFallback = useCallback(
    () => (
      <Box
        position="relative"
        width="100%"
        paddingTop={['150%', '130%', '56.25%']}
        overflow="hidden"
        borderRadius="4px"
      >
        <Box
          as="iframe"
          src={`${url}#view=FitH&embedded=true&zoom=100`}
          width="100%"
          height="100%"
          position="absolute"
          top="0"
          left="0"
          title={title || 'PDF Document'}
          aria-label={title || 'PDF Document'}
          role="document"
          allowFullScreen
          loading="lazy"
          onError={handleError}
        />
      </Box>
    ),
    [url, title, handleError],
  );

  const renderDownloadButton = useCallback(
    () => (
      <UiLink
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        padding={['10px 20px', '12px 24px']}
        backgroundColor="#28724F"
        color="white"
        borderRadius="100px"
        display="inline-block"
        fontWeight="500"
        fontSize={['14px', '16px']}
        _hover={{ backgroundColor: '#1E5F3E' }}
        aria-label={`Download ${title || 'PDF'}`}
        width={['100%', 'auto']}
        textAlign="center"
      >
        Download PDF
      </UiLink>
    ),
    [url, title],
  );

  const getPdfWidth = useCallback(() => {
    let calculatedWidth =
      width > 768 ? Math.min(width * 0.85, 900) : Math.min(width - 32, 600);

    if (pdfDimensions.width > 0 && pdfDimensions.height > 0) {
      const availableHeight = height - 500;

      if (
        (calculatedWidth / pdfDimensions.width) * pdfDimensions.height >
        availableHeight
      ) {
        const heightConstrainedWidth =
          (availableHeight / pdfDimensions.height) * pdfDimensions.width;
        calculatedWidth = Math.min(calculatedWidth, heightConstrainedWidth);
      }
    }

    return calculatedWidth;
  }, [width, height, pdfDimensions]);

  if (isLoading) {
    return (
      <Box
        width="100%"
        height={['200px', '250px', '300px']}
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="1px solid #E5E5E5"
        borderRadius="4px"
      >
        <Spinner size={['md', 'lg']} color="#28724F" />
      </Box>
    );
  }

  if (useIframeFallback) {
    return (
      <Box width="100%">
        <Box marginBottom={4}>{renderIframeFallback()}</Box>
        <Flex
          justifyContent="center"
          marginTop={4}
          flexDirection={['column', 'row']}
          gap={4}
        >
          {renderDownloadButton()}
        </Flex>
      </Box>
    );
  }

  return (
    <Box width="100%" ref={containerRef} tabIndex={0}>
      {isError ? (
        <Box
          padding={[4, 5, 6]}
          borderRadius="4px"
          backgroundColor="#f9f9f9"
          textAlign="center"
        >
          <Heading as="h3" size={['sm', 'md']} marginBottom={4}>
            PDF Preview Unavailable
          </Heading>
          <Text marginBottom={4} fontSize={['14px', '16px']}>
            {errorType === 'network' &&
              'The PDF could not be loaded due to a network error. Please try again later.'}
            {errorType === 'invalid_url' &&
              'The PDF URL is invalid or malformed.'}
            {errorType === 'access_denied' &&
              'You do not have permission to access this PDF.'}
            {(!errorType || errorType === 'unknown') &&
              'The PDF preview could not be displayed. Please download the document to view it.'}
          </Text>
          <Flex
            justifyContent="center"
            gap={4}
            flexDirection={['column', 'row']}
            width="100%"
          >
            {renderDownloadButton()}
            <UiLink
              as="button"
              onClick={retryLoading}
              padding={['10px 20px', '12px 24px']}
              backgroundColor="transparent"
              color="#28724F"
              border="1px solid #28724F"
              borderRadius="100px"
              display="inline-block"
              fontWeight="500"
              fontSize={['14px', '16px']}
              _hover={{ backgroundColor: 'rgba(40, 114, 79, 0.05)' }}
              width={['100%', 'auto']}
            >
              Retry
            </UiLink>
          </Flex>
        </Box>
      ) : (
        <Box borderRadius="4px" padding={[0, 0, 2]} backgroundColor="white">
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <Flex
                justifyContent="center"
                alignItems="center"
                height="300px"
                width="100%"
              >
                <Spinner size="lg" color="#28724F" />
              </Flex>
            }
            options={documentOptions}
          >
            <Flex
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              width="100%"
            >
              <Box position="relative" marginY={[2, 3, 4]}>
                <Page
                  pageNumber={pageNumber}
                  width={getPdfWidth()}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  scale={1.2}
                  className="pdf-page"
                  loading={
                    <Box
                      width={getPdfWidth()}
                      height={
                        pdfDimensions.height > 0
                          ? (getPdfWidth() / pdfDimensions.width) *
                            pdfDimensions.height
                          : 500
                      }
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Spinner size="md" color="#28724F" />
                    </Box>
                  }
                />
              </Box>

              <Flex
                justifyContent="space-between"
                alignItems="center"
                mt={2}
                flexDirection={['column', 'column', 'row']}
                gap={[4, 4, 0]}
                width="100%"
                paddingX={[0, 1, 4]}
              >
                <Box>
                  <Text
                    color="#949494"
                    fontSize={['14px', '16px']}
                    fontWeight="500"
                    textAlign={['center', 'center', 'left']}
                  >
                    Page {pageNumber} of {numPages}
                  </Text>
                </Box>

                <Box order={[3, 3, 2]} mt={[4, 4, 0]}>
                  {renderDownloadButton()}
                </Box>

                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={4}
                  height="32px"
                  transform={[
                    'translateY(0)',
                    'translateY(0)',
                    'translateY(10px)',
                  ]}
                  role="navigation"
                  aria-label="PDF navigation"
                  order={[2, 2, 3]}
                  width={['100%', '100%', 'auto']}
                  mt={[2, 2, 0]}
                  marginLeft={[0, 0, 4]}
                >
                  <UiLink
                    as="button"
                    disabled={pageNumber <= 1}
                    onClick={previousPage}
                    cursor={pageNumber <= 1 ? 'not-allowed' : 'pointer'}
                    aria-disabled={pageNumber <= 1}
                    aria-label="Previous page"
                    padding={2}
                  >
                    <LeftArrow pageNumber={pageNumber} />
                    <VisuallyHidden>Previous Page</VisuallyHidden>
                  </UiLink>

                  <UiLink
                    as="button"
                    disabled={pageNumber >= (numPages || 1)}
                    cursor={
                      pageNumber >= (numPages || 1) ? 'not-allowed' : 'pointer'
                    }
                    onClick={nextPage}
                    aria-disabled={pageNumber >= (numPages || 1)}
                    aria-label="Next page"
                    padding={2}
                  >
                    <RightArrow
                      pageNumber={pageNumber}
                      numPages={numPages || 1}
                    />
                    <VisuallyHidden>Next Page</VisuallyHidden>
                  </UiLink>
                </Box>
              </Flex>
            </Flex>
          </Document>
        </Box>
      )}
    </Box>
  );
};

export default PDFViewer;

interface ArrowProps {
  pageNumber: number;
  numPages?: number;
}

const LeftArrow = ({ pageNumber }: ArrowProps) => (
  <svg
    className="swiper-button-prev"
    width={32}
    height={32}
    style={{
      cursor: pageNumber <= 1 ? 'not-allowed' : 'pointer',
      width: '36px',
      height: '36px',
      position: 'relative',
    }}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-hidden="true"
  >
    <rect
      x="0.75"
      y="0.75"
      width="30.5"
      height="30.5"
      rx="15.25"
      stroke={pageNumber <= 1 ? '#949494' : '#28724F'}
      strokeWidth="1.5"
    />
    <path
      d="M17 11L12 16L17 21"
      stroke={pageNumber <= 1 ? '#949494' : '#28724F'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RightArrow = ({ pageNumber, numPages = 1 }: ArrowProps) => (
  <svg
    className="swiper-button-next"
    width={32}
    height={32}
    style={{
      cursor: pageNumber >= numPages ? 'not-allowed' : 'pointer',
      width: '36px',
      height: '36px',
      position: 'relative',
    }}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-hidden="true"
  >
    <rect
      x="0.75"
      y="0.75"
      width="30.5"
      height="30.5"
      rx="15.25"
      stroke={pageNumber >= numPages ? '#949494' : '#28724F'}
      strokeWidth="1.5"
    />
    <path
      d="M14 21L19 16L14 11"
      stroke={pageNumber >= numPages ? '#949494' : '#28724F'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
